import { invokeEdgeJson } from "@/lib/api/edge-invoke";

interface FulfilmentSyncResult {
  success: boolean;
  error?: string;
  provider_sync_status: string;
  idempotent?: boolean;
  message?: string;
}

// A successful HTTP invocation is not a provider acknowledgement. In particular,
// a completed operation identity need not contain a fresh shipment readback.
function fulfilmentAcknowledgement(invoked: {
  success: boolean;
  data: Record<string, unknown> | null;
  error?: string;
}): FulfilmentSyncResult {
  const payload: unknown = invoked.data;
  const result = payload && typeof payload === "object" && !Array.isArray(payload)
    ? payload as Record<string, unknown> : {};
  const error = invoked.error || (typeof result.error === "string" ? result.error : undefined);
  const status = result.provider_sync_status;
  const liveMode = result.mode === undefined || result.mode === "LIVE" || result.mode === "READBACK";
  const completedIdentity = result.code === "ALREADY_COMPLETED" && result.idempotent === true && status === undefined;
  const accepted = invoked.success && result.success === true && !error && liveMode
    && (status === "confirmed" || status === "pending" || completedIdentity);
  return {
    success: accepted,
    error: accepted ? undefined : error || "Channel sync was not confirmed. Refresh the shipment before retrying.",
    provider_sync_status: accepted ? (status === "confirmed" ? "confirmed" : "pending")
      : status === "remote_review" || status === "error" ? status : "pending",
    idempotent: typeof result.idempotent === "boolean" ? result.idempotent : undefined,
    message: typeof result.message === "string" ? result.message : undefined,
  };
}

export async function syncBigCommerceFulfilment(params: {
  connectorId: string;
  orgId: string;
  salesOrderId: string;
  shipmentId?: string | null;
}): Promise<{
  success: boolean;
  error?: string;
  provider_sync_status?: string;
  idempotent?: boolean;
  message?: string;
}> {
  const invoked = await invokeEdgeJson<{
    success?: boolean;
    error?: string;
    provider_sync_status?: string;
    idempotent?: boolean;
    message?: string;
  }>("bigcommerce-fulfilment-sync", {
    connector_id: params.connectorId,
    org_id: params.orgId,
    sales_order_id: params.salesOrderId,
    shipment_id: params.shipmentId ?? undefined,
    mode: "LIVE",
  }, { surface: "shipment" });
  return fulfilmentAcknowledgement(invoked);
}

export async function syncEbayFulfilment(params: {
  connectorId: string;
  orgId: string;
  salesOrderId: string;
  shipmentId?: string | null;
  mode?: "auto" | "readback";
}): Promise<{
  success: boolean;
  error?: string;
  provider_sync_status?: string;
  idempotent?: boolean;
  message?: string;
}> {
  const invoked = await invokeEdgeJson<{
    success?: boolean;
    error?: string;
    provider_sync_status?: string;
    idempotent?: boolean;
    message?: string;
  }>("ebay-fulfilment-sync", {
    connector_id: params.connectorId,
    org_id: params.orgId,
    sales_order_id: params.salesOrderId,
    shipment_id: params.shipmentId ?? undefined,
    mode: params.mode ?? "auto",
  }, { surface: "shipment" });
  return fulfilmentAcknowledgement(invoked);
}
